import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * A slide-out panel from the right, for one person's task list on Operations > Tasks.
 *
 * Rendered through a portal to document.body so no ancestor stacking context (a transform or
 * filter on the layout) can paint over it; HelpDrawer learned that on stage.
 *
 * Accessibility: role="dialog" with aria-modal and aria-labelledby; focus moves into the panel on
 * open, Tab and Shift+Tab stay inside it, Escape and a click on the backdrop close it, and the
 * caller returns focus to whatever opened it (`onClose` is the caller's cue). Page scroll is locked
 * while it is open. The slide is a transform, and is dropped for prefers-reduced-motion.
 *
 * Width: full width on a phone, up to 48rem from sm and 64rem from lg (never past 92vw), so the six
 * task columns and a long task group sit on one line rather than wrapping.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const TaskDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  /** Id of the element (inside `header`) that names the dialog. */
  titleId: string;
  /** The panel's header content, beside the close button. */
  header: React.ReactNode;
  closeLabel?: string;
  children: React.ReactNode;
}> = ({ open, onClose, titleId, header, closeLabel = 'Close the task list', children }) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  // Mounted off-screen, then slid in on the next frame so the transform transitions.
  const [entered, setEntered] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) {
      setEntered(false);
      return undefined;
    }
    const frame = window.requestAnimationFrame(() => setEntered(true));
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) {
        e.preventDefault();
        panelRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !panelRef.current.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !panelRef.current.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div
        data-testid="task-drawer-backdrop"
        aria-hidden="true"
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 motion-reduce:transition-none ${
          entered ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-gray-700 bg-gray-900 shadow-2xl transition-transform duration-300 ease-out focus:outline-none motion-reduce:transition-none sm:w-[min(48rem,92vw)] lg:w-[min(64rem,92vw)] ${
          entered ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div aria-hidden="true" className="h-1 shrink-0 bg-gradient-to-r from-blue-600 via-blue-500 to-violet-500" />
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-700 bg-gray-800/60 px-5 py-4">
          <div className="min-w-0 flex-1">{header}</div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            className="shrink-0 rounded-md p-1.5 text-gray-400 transition-colors hover:bg-white/[0.06] hover:text-white focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>
    </>,
    document.body,
  );
};
